import { computed, ref } from 'vue';

interface FeatureFlags {
    enable_v2_reviews: boolean;
    enable_v2_projects: boolean;
    enable_expert_wallet: boolean;
    require_expert_signup_access_key: boolean;
}

const serverFlags = ref<Partial<FeatureFlags>>({});
export const expertWalletAccessQueryParam = "shpx_expert_wallet_gate";
export const expertWalletAccessQueryValue = "enabled";
export const expertWalletAccessStorageKey = "shopexperts.expertWalletAccessGranted";

const getEnvFlag = (key: string, defaultValue = false): boolean => {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;
    return value === 'true' || value === '1';
};

const envFlags: FeatureFlags = {
    enable_v2_reviews: getEnvFlag('VITE_ENABLE_V2_REVIEWS', false),
    enable_v2_projects: getEnvFlag('VITE_ENABLE_V2_PROJECTS', false),
    enable_expert_wallet: getEnvFlag('VITE_ENABLE_EXPERT_WALLET', false),
    require_expert_signup_access_key: getEnvFlag('VITE_REQUIRE_EXPERT_SIGNUP_ACCESS_KEY', true),
};

type QueryValue = string | string[] | null | undefined;
type QueryLike = URLSearchParams | object;

const getQueryValue = (query: QueryLike | undefined, key: string): string | undefined => {
    if (!query) return undefined;
    if (query instanceof URLSearchParams) return query.get(key) ?? undefined;

    const value = (query as Record<string, QueryValue>)[key];
    return Array.isArray(value) ? value[0] : value ?? undefined;
};

export const grantExpertWalletAccessFromQuery = (query?: QueryLike): boolean => {
    if (getQueryValue(query, expertWalletAccessQueryParam) !== expertWalletAccessQueryValue) {
        return false;
    }

    sessionStorage.setItem(expertWalletAccessStorageKey, "true");
    return true;
};

export const hasExpertWalletSessionAccess = (): boolean => {
    return sessionStorage.getItem(expertWalletAccessStorageKey) === "true";
};

export const useFeatureFlags = () => {
    const flags = computed<FeatureFlags>(() => ({
        ...envFlags,
        ...serverFlags.value,
    }));

    const isEnabled = (flagName: keyof FeatureFlags): boolean => {
        return flags.value[flagName] ?? false;
    };

    const isExpertWalletEnabled = (query?: QueryLike): boolean => {
        return isEnabled("enable_expert_wallet") || grantExpertWalletAccessFromQuery(query) || hasExpertWalletSessionAccess();
    };

    const fetchServerFlags = async () => {
        // TODO: Implement API call to fetch flags from server
    };

    const setFlag = (flagName: keyof FeatureFlags, value: boolean) => {
        serverFlags.value = {
            ...serverFlags.value,
            [flagName]: value,
        };
    };

    return {
        flags,
        isEnabled,
        isExpertWalletEnabled,
        fetchServerFlags,
        setFlag,
    };
};
